import {
  AdjustScheduledCampaignWeek,
  adjustScheduledCampaignWeekSchema,
} from "@/components/campaign/configuration/AdjustScheduledCampaignWeek";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { getWeekPeriod, UTCToLocalDate } from "@/utils/dates";
import { createFileRoute, linkOptions } from "@tanstack/react-router";
import { useMemo } from "react";
import z from "zod";
import { Route as BuyCreditsRoute } from "./buy-credits";

const historyStateSchema = z
  .object({
    values: adjustScheduledCampaignWeekSchema.shape.schedules,
    skipCreditCheck: z.boolean(),
    autoSubmit: z.boolean(),
  })
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/adjust-week/$weekStart/",
)({
  staticData: {
    meta: {
      title: "Rediger uge",
    },
  },
  beforeLoad: ({ location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
    };
  },
  component: RouteComponent,
});

const backRouteOptions = linkOptions({
  from: Route.fullPath,
  to: "/campaigns/$id",
});

function RouteComponent() {
  const { weekStart } = Route.useParams();
  const context = Route.useRouteContext();

  const navigate = Route.useNavigate();

  const historyState = context.useHistoryState();

  const { data: campaign } = context.useCampaignQuery();

  const week = useMemo(
    () => getWeekPeriod(UTCToLocalDate(weekStart)),
    [weekStart],
  );

  return (
    <PageHeaderProvider
      options={{
        title: "Rediger uge",
      }}
    >
      <SettingsContainer backLinkOptions={backRouteOptions}>
        <AdjustScheduledCampaignWeek
          key={`${campaign.id},${week.start.getTime()}`}
          campaign={campaign}
          week={week}
          values={historyState?.values}
          skipCreditCheck={historyState?.skipCreditCheck}
          autoSubmit={historyState?.autoSubmit}
          goToBuyCredits={async ({ neededCredits, schedules }) => {
            const state = {
              routeStates: {
                [Route.id]: {
                  values: schedules,
                  skipCreditCheck: false,
                  autoSubmit: false,
                },
                [BuyCreditsRoute.id]: {
                  neededCredits,
                },
              },
            };

            await navigate({
              replace: true,
              state,
            });

            await navigate({
              to: "./buy-credits",
              state,
            });
          }}
          onSuccess={async () => {
            await navigate(backRouteOptions);
          }}
        />
      </SettingsContainer>
    </PageHeaderProvider>
  );
}
